import { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ColorsV2,
  Flex,
  getToken,
  Icon,
  Pane,
  spaces,
  Text,
  TextareaAutosize,
} from '@electricjs/arc';
import styled, { keyframes } from 'styled-components';

import { useCopilotKitAgentContext } from '@/components/CopilotKitProvider/CopilotKitAgentContext';
import { useNewChatAction } from '@/hooks/aiChat/useNewChatAction';
import { useAppDispatch } from '@/redux/store';
import { openDrawer } from '@/redux/slices/aiChatDrawerSlice';

// Quick prompts specific to the Support Center Gigawatt input's empty state.
// Unrelated to the AI chat drawer/full-page chat's own page-based quick prompts
// (src/constants/quickPrompts.ts) — this input has its own fixed set.
const QUICK_PROMPTS = [
  { leafKey: 'needAppAccess', icon: 'grid-2' },
  { leafKey: 'helpMdm', icon: 'shield-check' },
  { leafKey: 'computerSlow', icon: 'laptop' },
  { leafKey: 'openTicket', icon: 'ticket' },
] as const;

// Minimum time between sends, guarding against a second click landing before
// the disabled state has a chance to render (see lastSentAtRef).
const RESEND_COOLDOWN_MS = 500;

const rotateBorder = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
`;

const fadeIn = keyframes`
  to { opacity: 0.5; }
`;

const StyledPane = styled(Pane)`
  position: relative;
  overflow: visible;
  isolation: isolate;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: 0 0 6px 2px ${getToken(ColorsV2.PRIMARY_LIGHT)};
    opacity: 0;
    animation: ${fadeIn} 600ms ease-out forwards;
    pointer-events: none;
    z-index: -1;
  }
`;

const RotatingBorder = styled.div`
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;

  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
  padding: 2px;

  &::before {
    content: '';
    position: absolute;
    width: 200%;
    aspect-ratio: 1;
    top: 50%;
    left: 50%;
    background: conic-gradient(
      transparent 0%,
      transparent 60%,
      ${getToken(ColorsV2.PRIMARY_LIGHT)} 80%,
      transparent 100%
    );
    opacity: 0.6;
    animation: ${rotateBorder} 12s linear infinite;
  }
`;

const iconFadeIn = keyframes`
  to { opacity: 1; }
`;

const SparklesIcon = styled.div`
  display: flex;
  align-items: center;
  opacity: 0;
  animation: ${iconFadeIn} 600ms ease-out 200ms forwards;
`;

const InputWrapper = styled(Box)`
  position: relative;
  flex: 1;
  min-width: 0;

  /* Arc's TextareaAutosize renders an unstyled wrapper div with no width — target it directly */
  & > div {
    width: 100%;
  }

  & textarea {
    padding-right: 4rem;
  }
`;

const EmbeddedButton = styled(Button)`
  position: absolute;
  right: 0.6rem;
  top: 50%;
  transform: translateY(calc(-50% - 2px));
`;

const QuickPromptsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.8rem;
  width: 100%;
`;

// Arc's ButtonLabel sets white-space: nowrap, overflow: hidden, and
// text-overflow: ellipsis. Override all three so long prompt labels wrap
// to multiple lines instead of overflowing the button.
const QuickPromptButton = styled(Button)`
  height: auto;
  justify-content: flex-start;
  text-align: left;
  width: 100%;

  span {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    text-align: left;
  }
`;

export const GigawattSupportCenterInput = () => {
  const { t } = useTranslation('support-center');
  const dispatch = useAppDispatch();

  const { messages, sendMessage, isLoading, isError } =
    useCopilotKitAgentContext();
  const { handleNewChat } = useNewChatAction(undefined, isLoading);

  const [inputValue, setInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Holds { text, id } when a send must be deferred until CopilotKit re-renders
  // with the new threadId (post new-chat creation).
  const [pendingMessage, setPendingMessage] = useState<{
    text: string;
    id: string;
  } | null>(null);
  // Guards against the effect re-firing if sendMessage changes reference while
  // pendingMessage is still set (before setPendingMessage(null) commits).
  const pendingSentRef = useRef(false);
  // On the immediate-send path, isSubmitting is set and cleared within the
  // same synchronous call, so a second click landing right after the first
  // (before React ever renders the disabled state) wouldn't be blocked by
  // isSubmitting alone. This timestamp guard closes that gap independent of
  // render timing.
  const lastSentAtRef = useRef(0);

  const hasExistingMessages = messages.some(m => m.role === 'user');

  // Deferred send: fires after React re-renders CopilotKit with the new threadId,
  // ensuring the message targets the correct conversation. The drawer opens
  // before this effect runs — the message appears in the next frame, which is
  // invisible to the user.
  useEffect(() => {
    if (!pendingMessage || pendingSentRef.current) return;
    pendingSentRef.current = true;
    sendMessage({
      id: pendingMessage.id,
      role: 'user',
      content: pendingMessage.text,
    });
    setPendingMessage(null);
    setInputValue('');
    setIsSubmitting(false);
    return () => {
      // Reset on unmount so isSubmitting doesn't stay true if navigation occurs
      // between setPendingMessage and this effect firing.
      setIsSubmitting(false);
    };
  }, [pendingMessage, sendMessage]);

  const sendChatMessage = async (messageText: string) => {
    const now = Date.now();
    if (
      !messageText.trim() ||
      isSubmitting ||
      isError ||
      now - lastSentAtRef.current < RESEND_COOLDOWN_MS
    ) {
      return;
    }
    lastSentAtRef.current = now;
    setIsSubmitting(true);
    // Capture id here so it's stable across the deferral boundary.
    const messageId = `msg-${Date.now()}-${Math.random()}`;

    if (hasExistingMessages) {
      const success = await handleNewChat();
      if (!success) {
        setIsSubmitting(false);
        return;
      }
      // Defer send to next render so CopilotKit picks up the new threadId
      pendingSentRef.current = false;
      setPendingMessage({ text: messageText, id: messageId });
    } else {
      sendMessage({ id: messageId, role: 'user', content: messageText });
      setInputValue('');
      setIsSubmitting(false);
    }

    dispatch(openDrawer());
  };

  const handleSubmit = () => sendChatMessage(inputValue);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <StyledPane
      elevated
      width="100%"
      maxWidth="60rem"
      padding="1.2rem 1.6rem"
      margin="0 auto">
      <RotatingBorder />
      <Flex vertical width="100%">
        <Flex alignItems="center" gap="1rem" width="100%">
          <SparklesIcon>
            <Icon icon="sparkles" intent="primary" />
          </SparklesIcon>
          <InputWrapper>
            <TextareaAutosize
              id="gigawatt-support-center-input"
              aria-label={t('gigawattInput.ariaLabel')}
              placeholder={t('gigawattInput.placeholder')}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              minRows={1}
              maxRows={4}
            />
            <EmbeddedButton
              id="gigawatt-support-center-send-button"
              aria-label={t('gigawattInput.sendButtonLabel')}
              iconAfter="paper-plane"
              onClick={handleSubmit}
              disabled={!inputValue.trim()}
              loading={isSubmitting}
              variant="fill"
              buttonSize="small"
            />
          </InputWrapper>
        </Flex>
        <Box mt={spaces.space_s_rem} ml="3.6rem">
          <Text
            variant="body-2"
            color={ColorsV2.TEXT_LIGHT}
            textAlign="left"
            mb={spaces.space_xs_rem}>
            {t('gigawattInput.quickPrompts.tryAsking')}
          </Text>
          <QuickPromptsGrid
            role="list"
            aria-label={t('gigawattInput.quickPrompts.ariaLabel')}>
            {QUICK_PROMPTS.map(({ leafKey, icon }) => {
              const message = t(`gigawattInput.quickPrompts.${leafKey}`);
              return (
                <Box key={leafKey} role="listitem">
                  <QuickPromptButton
                    id={`gigawatt-support-center-quick-prompt-${leafKey}`}
                    variant="outline"
                    iconBefore={icon}
                    onClick={() => sendChatMessage(message)}
                    disabled={isSubmitting || isError}>
                    {message}
                  </QuickPromptButton>
                </Box>
              );
            })}
          </QuickPromptsGrid>
        </Box>
        {hasExistingMessages && (
          <Text variant="body-2" color={ColorsV2.DISABLED} ml="3.6rem">
            <Trans
              i18nKey="gigawattInput.existingChatWarning"
              ns="support-center"
              components={{
                Link: (
                  <Button
                    id="gigawatt-support-center-open-drawer"
                    variant="link"
                    display="inline"
                    buttonSize="small"
                    onClick={() => dispatch(openDrawer())}>
                    {' '}
                  </Button>
                ),
              }}
            />
          </Text>
        )}
      </Flex>
    </StyledPane>
  );
};
